import React, { useCallback, useContext, useMemo } from 'react';
import { t } from '@sm/intl';
import { Typography } from '@wds/typography';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { IconCalendar, IconGovBuilding, IconChevronRight } from '@wds/icons';

import { COMPARISON_PACKAGES, MONTHLY_PACKAGES } from '~app/pages/Pricing/lib/constants';
import { Packages, PackageDisplayNames } from '~shared/constants/pricing';
import { PackageData, PACKAGE_PRICE_TYPE } from '~shared/types/pricing/package';
import { PackageHeaderProps } from '~pricing/components/Summary/types';

import usePackageHeaderStyles from './useStyles';
import PackageDefs from '~pricing/lib/packages/packageDefs';
import PackageCta from '../PackageCta';
import PackagePrice from '~pricing/components/PackagePrice';
import PackageByline from '~pricing/components/PackageHeader/PackageByline';
import Ribbon from '../Ribbon';
import ToggleSwitch from '../ToggleSwitch';
import UserSelector from '../UserSelector';

import {
  formatMonthlyCostInLocaleCurrency,
  formatCostInLocaleCurrency,
  getCoreCostFromSkus,
  formatCostInSkuCurrency,
} from '~app/pages/Pricing/lib/lib-helpers';

import { usePackages } from '~shared/hooks/usePackages';
import usePriceDisplay from '~pricing/hooks/usePriceDisplay';
import useStarterEnabled from '~app/pages/Pricing/hooks/useStarterEnabled';

import COPY from '~pricing/components/PackageHeader/copy';
import PRICING_COPY from '~pricing/hooks/usePriceDisplay/copy';
import EXPERIMENT_COPY from '../../copy/experimentPackageCopy';
import {
  TogglePackages,
  PackageDisplayNames as Grow2792PackageDisplayNames,
  PackageDefs as Grow2792PackageDefs,
} from '../../constants';
import EduContext from '~shared/hooks/eduContext';
import { usePricingState } from '~shared/hooks/usePricingState';
import determinePackageSwap from '../../helpers';
import ACTIONS from '~shared/actions';
import { CombinedActions } from '~shared/types/pricing/actions';

const PACKAGE_SAVINGS = [
  Packages.AdvantageAnnual,
  Packages.TeamAdvantage,
  Packages.PremierAnnual,
  Packages.TeamPremier,
];
const expPackageDisplayNames = { ...PackageDisplayNames, ...Grow2792PackageDisplayNames };
const expPackageDefs = { ...PackageDefs, ...Grow2792PackageDefs };

export default function PackageHeader({
  packageName,
  comparisonSkuCosts,
  skuCosts,
  isSummaryPage = false,
  discountInfo,
}: PackageHeaderProps): JSX.Element {
  const { Header, Byline, CTA, Discount, DiscountApplied, SwitchForDiscount, ToggleContainer } = usePackageHeaderStyles(
    {
      isSummaryPage,
    }
  );
  const { isStarterEnabled } = useStarterEnabled();
  const isMonthlyPackage = useMemo(() => MONTHLY_PACKAGES.includes(packageName), [packageName]);

  const comparisonPackageName = COMPARISON_PACKAGES[packageName];
  const { allPackages: packageData } = usePackages();
  const { headerCost, headerDiscountedCost, headerByline, headerUnit } = usePriceDisplay(
    packageName,
    skuCosts,
    discountInfo
  );

  const {
    state: {
      grow2792: { advantageUsers, premierUsers },
    },
    dispatch,
  } = usePricingState();

  const isAdvantagePackage = [Packages.AdvantageAnnual, Packages.AdvantageMonthly, Packages.TeamAdvantage].includes(
    packageName
  );
  const isPremierPackage = [Packages.PremierAnnual, Packages.TeamPremier].includes(packageName);

  const coreSeatCost = useMemo(() => getCoreCostFromSkus(skuCosts), [skuCosts]);

  const annualFormattedCost = useMemo(() => {
    const userMultiplier = (): number => {
      if (isAdvantagePackage) {
        return advantageUsers;
      }

      if (isPremierPackage) {
        return premierUsers;
      }

      return 1;
    };

    if (coreSeatCost) {
      return isMonthlyPackage
        ? formatCostInSkuCurrency(((coreSeatCost.cost * userMultiplier()) / 100) * 12, coreSeatCost)
        : formatCostInSkuCurrency((coreSeatCost.cost * userMultiplier()) / 100, coreSeatCost);
    }

    return '';
  }, [advantageUsers, coreSeatCost, isAdvantagePackage, isMonthlyPackage, isPremierPackage, premierUsers]);

  const comparisonCoreSeatCost = useMemo(
    () => comparisonSkuCosts && getCoreCostFromSkus(comparisonSkuCosts),
    [comparisonSkuCosts]
  );

  const comparisonFormattedPrice = useMemo(
    () => comparisonCoreSeatCost && formatCostInLocaleCurrency(comparisonCoreSeatCost),
    [comparisonCoreSeatCost]
  ) as string;

  const comparisonFormattedMonthlyPrice = useMemo(
    () => comparisonCoreSeatCost && formatMonthlyCostInLocaleCurrency(comparisonCoreSeatCost),
    [comparisonCoreSeatCost]
  ) as string;

  const comparisonPackage = useMemo(
    () =>
      comparisonPackageName
        ? packageData?.find((packageObj: PackageData) => packageObj.packageName === comparisonPackageName)
        : null,
    [comparisonPackageName, packageData]
  );

  const comparisonSeatCost = useMemo(
    () => (comparisonPackage ? getCoreCostFromSkus(comparisonPackage?.skuCost) : null),
    [comparisonPackage]
  );

  const handleSwitchAndSave = useCallback((): void => {
    const numberOfUsers = '3';

    determinePackageSwap(numberOfUsers, isAdvantagePackage, isPremierPackage, dispatch);

    dispatch({
      type: ACTIONS.PNP_REFRESH_CHANGE_USERS,
      payload: { users: numberOfUsers, packageType: isAdvantagePackage ? 'advantageUsers' : 'premierUsers' },
    } as CombinedActions);

    dispatch({
      type: ACTIONS.PNP_REFRESH_TOGGLE_SWITCH,
      payload: {
        packageToBeReplaced: packageName,
        replacedWith:
          packageName === Packages.TeamAdvantage && Number(numberOfUsers) === 1
            ? Packages.AdvantageAnnual
            : Packages.TeamAdvantage,
      },
    } as CombinedActions);
  }, [dispatch, isAdvantagePackage, isPremierPackage, packageName]);

  const getRibbon = useMemo(() => {
    const ribbonText = expPackageDefs[packageName]?.highlighted ? t(COPY.BEST_VALUE) : null;

    if (ribbonText && isAdvantagePackage) {
      return <Ribbon packageName={packageName}>{ribbonText}</Ribbon>;
    }

    return null;
  }, [isAdvantagePackage, packageName]);

  const { isEdu } = useContext(EduContext);

  const seatSavingsPercentage = useMemo(() => {
    let percentage = 0;

    if (comparisonPackage && coreSeatCost && comparisonSeatCost) {
      const costDifference = Math.abs(comparisonSeatCost?.cost - coreSeatCost?.cost);
      const equation =
        packageName === Packages.TeamAdvantage || packageName === Packages.TeamPremier
          ? costDifference / comparisonSeatCost?.cost
          : costDifference / coreSeatCost?.cost;
      percentage = Math.floor(equation * 100);
    }

    return percentage;
  }, [comparisonPackage, comparisonSeatCost, coreSeatCost, packageName]);

  const getSavings = (): JSX.Element => {
    if ((isAdvantagePackage && advantageUsers > 1) || (isPremierPackage && premierUsers > 1)) {
      return <h4 className={`${Discount} ${DiscountApplied}`}>{seatSavingsPercentage}% multi-user discount applied</h4>;
    }

    return (
      <Box className={`${Discount} ${SwitchForDiscount}`}>
        <Button variant="text" color="link" size="md" onClick={handleSwitchAndSave}>
          Switch to 3 users to save {seatSavingsPercentage}% <IconChevronRight title="Right arrow" />
        </Button>
      </Box>
    );
  };

  const bylineText =
    packageName === Packages.TeamAdvantage || packageName === Packages.TeamPremier
      ? t(PRICING_COPY.BILLED_ANNUALLY, { price: annualFormattedCost })
      : headerByline;

  return (
    <>
      {getRibbon}

      <header data-testid="PackageHeader__Header" className={Header}>
        <Box className={ToggleContainer}>
          {!isEdu && !isStarterEnabled && TogglePackages.includes(packageName) && (
            <ToggleSwitch packageName={packageName} />
          )}
        </Box>

        <Typography component="h2" data-testid="PackageHeader__HeaderText" align="center">
          {t(
            packageName === Packages.StandardMonthly
              ? expPackageDisplayNames.STARTER_MONTHLY
              : expPackageDisplayNames[packageName]
          )}
        </Typography>
        {packageName !== Packages.Enterprise && (
          <PackagePrice
            packagePriceType={isSummaryPage ? PACKAGE_PRICE_TYPE.SUMMARY : PACKAGE_PRICE_TYPE.DETAIL}
            costUnit={headerUnit}
            formattedPrice={headerDiscountedCost ?? headerCost}
          />
        )}
        {![Packages.Enterprise, Packages.Basic].includes(packageName) && headerDiscountedCost && (
          <PackagePrice
            packagePriceType={PACKAGE_PRICE_TYPE.COMPARISON}
            costUnit={headerUnit}
            formattedPrice={headerCost}
          />
        )}
        {comparisonFormattedPrice && !headerDiscountedCost && (
          <PackagePrice
            packagePriceType={PACKAGE_PRICE_TYPE.COMPARISON}
            costUnit={headerUnit}
            formattedPrice={
              packageName === Packages.StandardMonthly ? comparisonFormattedPrice : comparisonFormattedMonthlyPrice
            }
          />
        )}

        {packageName === Packages.Enterprise && (
          <h3>
            <IconGovBuilding title="Building Icon" />
          </h3>
        )}

        <Box className={Byline}>
          {packageName !== Packages.Enterprise && <IconCalendar title="Calendar Icon" size="sm" />}
          <Box>
            <PackageByline
              byline={
                packageName === Packages.Enterprise
                  ? EXPERIMENT_COPY.GROW_2792_ENT_PLATINUM_BYLINE.defaultMessage
                  : bylineText
              }
              color="dark"
            />
            {PACKAGE_SAVINGS.includes(packageName) && getSavings()}
          </Box>
        </Box>

        <UserSelector packageName={packageName} />

        <PackageCta packageName={packageName} isSummaryPage={isSummaryPage} className={CTA} />
      </header>
    </>
  );
}
